import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Plus, Search, MapPin, Trophy, Link2, CalendarCheck, BarChart3, ChevronRight } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { ROUTES } from '@/constants/routes';

const useCommunityPulse = () =>
  useQuery({
    queryKey: ['community-pulse'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [players, clubs] = await Promise.all([
        supabase.from('global_rankings_cache').select('rank', { count: 'exact', head: true }),
        supabase.from('padel_clubs_public').select('id', { count: 'exact', head: true }),
      ]);
      return { players: players.count ?? 0, clubs: clubs.count ?? 0 };
    },
  });

const GROUP_LINK = /groups\/([0-9a-f-]{36})/i;

export const NoGroupHome: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useCommunityPulse();
  const [link, setLink] = useState('');

  const openLink = () => {
    const match = link.match(GROUP_LINK);
    if (!match) {
      toast.error('Paste the group link your friend shared');
      return;
    }
    navigate(ROUTES.GROUP_DETAIL(match[1]));
  };

  const stats = [
    { label: 'Players ranked', value: data?.players },
    { label: 'Padel clubs', value: data?.clubs },
  ];

  return (
    <div className="space-y-4 lg:col-span-2">
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-2xl font-bold text-primary tabular-nums">
              {s.value ? s.value.toLocaleString() : '—'}
            </p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-primary/40 bg-primary/10 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/20 p-2"><Users className="h-5 w-5 text-primary" /></div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-foreground">Start your padel group</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Bring your regular crew. Invite 3 friends and LuvALL handles the rest.
            </p>
          </div>
        </div>
        <ul className="mt-3 grid gap-1.5 text-sm text-foreground sm:grid-cols-3">
          <li className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-primary" /> Availability polls</li>
          <li className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Fair, balanced matches</li>
          <li className="flex items-center gap-2"><Trophy className="h-4 w-4 text-primary" /> Rankings & tournaments</li>
        </ul>
        <Button className="mt-4 w-full min-h-[44px] gap-2" onClick={() => navigate(ROUTES.CREATE_GROUP)}>
          <Plus className="h-4 w-4" /> Create group & invite friends
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Link2 className="h-4 w-4 text-primary" /> Got an invite link?
        </h3>
        <div className="mt-2 flex gap-2">
          <Input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste the group link"
            className="min-h-[44px]"
            onKeyDown={(e) => e.key === 'Enter' && openLink()}
          />
          <Button variant="secondary" className="min-h-[44px]" onClick={openLink}>Open</Button>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {[
          { icon: Search, label: 'Find a group to join', to: ROUTES.GROUPS },
          { icon: MapPin, label: 'Courts near me', to: '/padel-courts-near-me' },
          { icon: Trophy, label: 'Global rankings', to: ROUTES.RANKINGS },
        ].map((a) => (
          <button
            key={a.label}
            type="button"
            onClick={() => navigate(a.to)}
            className="flex min-h-[52px] items-center gap-3 rounded-xl border border-border bg-card px-4 text-left text-sm font-medium text-foreground transition-colors hover:border-primary/50"
          >
            <a.icon className="h-4 w-4 text-primary" />
            <span className="flex-1">{a.label}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default NoGroupHome;
